import { PREPARAT_PARTITIVE } from '@/lib/pl-plural';
import type { CitationScope } from '@/types/report';

// ---------------------------------------------------------------------------
// Source-type system: a single registry maps a citation/finding source_type to
// its display label and color treatment (chip = inline citation, badge = number
// pill + excerpt tag, dot = legend swatch). Extends the original v1 palette
// (pubmed/fda/pharmindex) with lactmed (teal), tga, ema, bdpm, etc.
// ---------------------------------------------------------------------------
export interface SourceMeta {
  label: string; // short source-type name, e.g. "PubMed"
  kind: string; // plain-language description of what this source IS
  locatorLabel: string; // human label for the citation locator/id
  chip: string; // inline citation chip
  badge: string; // numbered pill in the citation list + excerpt source tag
  dot: string; // legend swatch
}

const SOURCE_META: Record<string, SourceMeta> = {
  pubmed: {
    label: 'PubMed',
    kind: 'Recenzowana literatura medyczna (NCBI, USA)',
    locatorLabel: 'PMID',
    chip: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
  },
  fda: {
    label: 'FDA',
    kind: 'Etykieta rejestracyjna leku (FDA, USA)',
    locatorLabel: 'Nr wniosku',
    chip: 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    dot: 'bg-amber-500',
  },
  lactmed: {
    label: 'LactMed',
    kind: 'Baza bezpieczeństwa leków w laktacji (LactMed, USA)',
    locatorLabel: 'Monografia',
    chip: 'bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200',
    badge: 'bg-teal-100 text-teal-800 border-teal-200',
    dot: 'bg-teal-500',
  },
  pharmindex: {
    label: 'PharmIndex',
    kind: 'Baza leków (PharmIndex, PL)',
    locatorLabel: 'ID leku',
    chip: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    dot: 'bg-purple-500',
  },
  rpl: {
    label: 'ChPL',
    kind: 'Charakterystyka Produktu Leczniczego (ChPL, Polska)',
    locatorLabel: 'ID produktu',
    chip: 'bg-cyan-100 text-cyan-800 border-cyan-200 hover:bg-cyan-200',
    badge: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    dot: 'bg-cyan-500',
  },
  bdpm: {
    label: 'ChPL Francja (ANSM)',
    kind: 'Charakterystyka Produktu Leczniczego (RCP, Francja)',
    locatorLabel: 'CIS',
    chip: 'bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200',
    badge: 'bg-sky-100 text-sky-800 border-sky-200',
    dot: 'bg-sky-500',
  },
  tga: {
    label: 'TGA',
    kind: 'Klasyfikacja stosowania leków w ciąży (TGA, Australia)',
    locatorLabel: 'Nazwa substancji',
    chip: 'bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500',
  },
  ema: {
    label: 'EMA',
    kind: 'Europejska Agencja Leków (EMA, UE)',
    locatorLabel: 'Identyfikator',
    chip: 'bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200',
    badge: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    dot: 'bg-indigo-500',
  },
  knowledge_base: {
    label: 'Baza wiedzy',
    kind: 'Wewnętrzna baza wiedzy medycznej',
    locatorLabel: 'Źródło',
    chip: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
    badge: 'bg-green-100 text-green-800 border-green-200',
    dot: 'bg-green-500',
  },
};

const DEFAULT_SOURCE_META: SourceMeta = {
  label: 'Inne źródło',
  kind: 'Źródło dodatkowe',
  locatorLabel: 'Identyfikator',
  chip: 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200',
  badge: 'bg-gray-100 text-gray-800 border-gray-200',
  dot: 'bg-gray-400',
};

export function sourceMeta(type?: string): SourceMeta {
  return (type && SOURCE_META[type]) || DEFAULT_SOURCE_META;
}

// ---------------------------------------------------------------------------
// ChPL consensus cue. A molecule's Polish section 4.6 is product-scoped: for
// progesterone, four preparations share one permissive wording while Crinone
// alone is far more restrictive. The backend stamps how many preparations share
// the cited text (`support`) out of how many were compared (`support_total`),
// so the citation can say which of the two it is.
// ---------------------------------------------------------------------------

// "ChPL · 4 z 8 preparatów", or undefined when there is nothing worth saying:
// the counts are missing (a report generated before the ChPL fan-out), are
// incoherent, or only one preparation exists — "1 z 1" is noise, not consensus.
export function chplSupportLabel(support?: number, total?: number): string | undefined {
  const counts = chplSupport(support, total);
  if (!counts) return undefined;
  return `ChPL · ${counts.support} z ${counts.total} ${PREPARAT_PARTITIVE}`;
}

// True when the cited wording is one product's, not the molecule's shared
// position — the case a clinician must not read as "the Polish position".
export function isChplOutlier(support?: number, total?: number): boolean {
  return chplSupport(support, total)?.support === 1;
}

function chplSupport(
  support?: number,
  total?: number,
): { support: number; total: number } | undefined {
  if (support == null || total == null) return undefined;
  if (!Number.isInteger(support) || !Number.isInteger(total)) return undefined;
  if (total <= 1 || support < 1 || support > total) return undefined;
  return { support, total };
}

export function scopeLabel(
  scope?: CitationScope | null,
): { text: string; title: string } | undefined {
  const form = scope?.form?.trim();
  const route = scope?.route?.trim();
  const text = [form, route].filter(Boolean).join(' · ');
  if (!text) return undefined;
  const title = [form && `postać: ${form}`, route && `droga podania: ${route}`]
    .filter(Boolean)
    .join(' · ');
  return { text, title };
}

// Short one-line gloss under a pregnancy-category system name (TGA / FDA / …).
// Keeps the acronym as the scan target while explaining what it is.
const PREGNANCY_SYSTEM_GLOSS: Record<string, string> = {
  tga: 'Klasyfikacja ciąży · Australia',
  fda: 'Etykieta leku (PLLR) · USA',
  ema: 'Europejska Agencja Leków · UE',
  lactmed: 'Baza laktacyjna · USA',
  rpl: 'ChPL · Polska',
};

export function pregnancySystemGloss(system?: string): string | undefined {
  const key = (system ?? '').trim().toLowerCase();
  if (!key) return undefined;
  if (PREGNANCY_SYSTEM_GLOSS[key]) return PREGNANCY_SYSTEM_GLOSS[key];
  // Fuzzy match ("TGA Australia", …)
  for (const [id, gloss] of Object.entries(PREGNANCY_SYSTEM_GLOSS)) {
    if (key.includes(id)) return gloss;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Evidence-tier (study-type) system: maps a PubMed publication tier to its
// Polish study-type label + color treatment. Shared by the "Relevant
// literature" panel and the per-finding study-type tag next to a citation.
// ---------------------------------------------------------------------------
const EVIDENCE_TIER_LABEL: Record<string, string> = {
  guideline: 'Wytyczne',
  'meta-analysis': 'Metaanaliza',
  RCT: 'Badanie randomizowane',
  cohort: 'Badanie obserwacyjne',
  review: 'Przegląd',
  'case report': 'Opis przypadku',
  editorial: 'Komentarz',
  other: 'Inne',
  retracted: 'Wycofane',
};

export function evidenceTierLabel(tier?: string): string {
  const normalized = (tier || '').trim();
  if (!normalized) return 'Bez kategorii';
  return EVIDENCE_TIER_LABEL[normalized] ?? normalized;
}

export function evidenceTierClass(tier?: string): string {
  switch (tier) {
    case 'guideline':
    case 'meta-analysis':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    case 'RCT':
      return 'border-blue-200 bg-blue-50 text-blue-800';
    case 'cohort':
      return 'border-sky-200 bg-sky-50 text-sky-800';
    case 'review':
      return 'border-violet-200 bg-violet-50 text-violet-800';
    case 'case report':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    case 'editorial':
      return 'border-slate-200 bg-slate-50 text-slate-700';
    case 'retracted':
      return 'border-red-200 bg-red-50 text-red-800';
    default:
      return 'border-border bg-muted text-muted-foreground';
  }
}

// ---------------------------------------------------------------------------
// W4 clinical-practice relevance flags: `report.practice_flags` maps a
// source_id (e.g. "pubmed:26991890") to a Polish reason why that source's
// route/protocol/population is not used in Polish clinical practice. The
// single lookup helper every surface (finding rows, decision-support chips,
// literature panel) shares; the pill itself is practice-flag-badge.tsx.
// ---------------------------------------------------------------------------
export const PRACTICE_FLAG_LABEL = 'Niestandardowa praktyka';

// The flag reason for a source_id, or undefined when the source is unflagged
// (or the report carries no flags at all — legacy reports render unchanged).
// Blank reasons count as unflagged so a degenerate backend value can never
// render an empty tooltip.
export function practiceFlag(
  flags: Record<string, string> | undefined,
  sourceId: string | undefined,
): string | undefined {
  if (!flags || !sourceId) return undefined;
  const reason = flags[sourceId]?.trim();
  return reason || undefined;
}

// ---------------------------------------------------------------------------
// Origin cue (Phase 5): the coarse Poland-vs-international distinction used by the
// source-conflict callout. The Polish official position comes from the RPL
// (ChPL/SmPC); every other family is "international". Reuses the per-source
// labels/colors above — this only adds the origin grouping on top.
// ---------------------------------------------------------------------------
type SourceOriginKey = 'pl' | 'international';

export interface SourceOrigin {
  key: SourceOriginKey;
  flag: string; // short glyph cue (🇵🇱 vs 🌍)
  label: string; // Polish UI label for the origin group
}

const SOURCE_ORIGIN: Record<SourceOriginKey, SourceOrigin> = {
  pl: { key: 'pl', flag: '🇵🇱', label: 'ChPL (Polska)' },
  international: { key: 'international', flag: '🌍', label: 'Źródła międzynarodowe' },
};

// The one source family whose findings carry the Polish official position.
export function sourceOrigin(type?: string): SourceOrigin {
  return type === 'rpl' ? SOURCE_ORIGIN.pl : SOURCE_ORIGIN.international;
}
